import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ccUsageApi } from '../lib/api.js';
import {
  MAX_RANGE_PRESETS,
  canUseMaxRange,
  deriveTimeseries,
  deriveTimeseriesByGroup,
  isCcUsageParamsValid,
} from '../lib/cc-usage-derive.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  CcUsageQueryParams,
  CcUsageTimeseriesByGroupParams,
  CcUsageTimeseriesParams,
} from '../types/api.js';

/**
 * 365d/day 固定の max-range クエリ。range/granularity/from/to を除外したキーで
 * TanStack Query のキー重複排除が効き、timeseriesByGroup と timeseries で共有される。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business max-range プリフェッチクエリ
 * @graph-connects /cc-usage/timeseries-by-group [calls] cc-usage 積み上げ時系列取得 (365d/day)
 */
function useMaxRangeQuery(params: CcUsageTimeseriesByGroupParams, baseEnabled: boolean) {
  const shouldPrefetch = baseEnabled && canUseMaxRange(params);

  const maxKeyParams = {
    scope: params.scope,
    team: params.team,
    email: params.email,
    groupBy: params.groupBy,
    excludeTeams: params.excludeTeams,
    excludeNicknames: params.excludeNicknames,
  };

  return useQuery({
    queryKey: queryKeys.ccUsage.maxRangeTimeseriesByGroup(maxKeyParams),
    queryFn: () =>
      ccUsageApi.getTimeseriesByGroup({
        ...params,
        range: '365d',
        granularity: 'day',
        from: undefined,
        to: undefined,
      }),
    enabled: shouldPrefetch,
    staleTime: Infinity,
  });
}

/**
 * Claude Code 利用量サマリ。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage summary fetch hook
 * @graph-connects /cc-usage/summary [calls] cc-usage サマリ取得
 */
export function useCcUsageSummary(params: CcUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccUsage.summary(params),
    queryFn: () => ccUsageApi.getSummary(params),
    enabled: isCcUsageParamsValid(params),
  });
}

/**
 * Claude Code 利用量の時系列推移。
 *
 * max-range データが利用可能なら全 group を合算して simple timeseries を導出する。
 * scope=member / range=custom のときは従来通り直接フェッチ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage timeseries fetch hook (max-range 導出対応)
 * @graph-connects /cc-usage/timeseries [calls] cc-usage 時系列取得
 */
export function useCcUsageTimeseries(params: CcUsageTimeseriesParams) {
  const baseEnabled = isCcUsageParamsValid(params);
  const useMaxRange = baseEnabled && params.scope !== 'member';
  const maxRange = useMaxRangeQuery(
    { ...params, groupBy: params.scope === 'team' ? 'nickname' : 'team' },
    useMaxRange,
  );

  const directQuery = useQuery({
    queryKey: queryKeys.ccUsage.timeseries(params),
    queryFn: () => ccUsageApi.getTimeseries(params),
    enabled: baseEnabled && !maxRange.data,
  });

  const range = params.range ?? '30d';
  const granularity = params.granularity ?? 'day';

  const derived = useMemo(() => {
    if (!maxRange.data) return undefined;
    if (!MAX_RANGE_PRESETS.has(range)) return undefined;
    return deriveTimeseries(maxRange.data, range, granularity);
  }, [maxRange.data, range, granularity]);

  return {
    ...directQuery,
    data: derived ?? directQuery.data,
    isLoading: maxRange.data ? false : directQuery.isLoading,
    isPending: maxRange.data ? false : directQuery.isPending,
  };
}

/**
 * 積み上げ用 timeseries (team / nickname 別)。
 *
 * 初回は指定パラメータで直接フェッチしつつ、裏で 365d/day の max-range をプリフェッチする。
 * max-range データが利用可能になったら、以降の range/granularity 切り替えはクライアント側で即時導出。
 *
 * scope=member の場合は backend が拒否するためフロント側で無効化する。
 * scope=team & groupBy=team の組合せも同様に無効化。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage timeseries-by-group fetch hook (max-range 導出対応)
 * @graph-connects /cc-usage/timeseries-by-group [calls] cc-usage 積み上げ時系列取得
 */
export function useCcUsageTimeseriesByGroup(params: CcUsageTimeseriesByGroupParams) {
  const baseEnabled =
    isCcUsageParamsValid(params) &&
    params.scope !== 'member' &&
    !(params.scope === 'team' && params.groupBy === 'team');

  const maxRange = useMaxRangeQuery(params, baseEnabled);

  const directQuery = useQuery({
    queryKey: queryKeys.ccUsage.timeseriesByGroup(params),
    queryFn: () => ccUsageApi.getTimeseriesByGroup(params),
    enabled: baseEnabled && !maxRange.data,
  });

  const range = params.range ?? '30d';
  const granularity = params.granularity ?? 'day';

  const derived = useMemo(() => {
    if (!maxRange.data) return undefined;
    if (!MAX_RANGE_PRESETS.has(range)) return undefined;
    return deriveTimeseriesByGroup(maxRange.data, range, granularity);
  }, [maxRange.data, range, granularity]);

  return {
    ...directQuery,
    data: derived ?? directQuery.data,
    isLoading: maxRange.data ? false : directQuery.isLoading,
    isPending: maxRange.data ? false : directQuery.isPending,
  };
}

/**
 * Claude Code 利用量の user 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage by user fetch hook
 * @graph-connects /cc-usage/by-user [calls] cc-usage user 別 breakdown 取得
 */
export function useCcUsageByUser(params: CcUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccUsage.byUser(params),
    queryFn: () => ccUsageApi.getByUser(params),
    enabled: isCcUsageParamsValid(params),
  });
}

/**
 * Claude Code 利用量の repository 別 breakdown。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage by repository fetch hook
 * @graph-connects /cc-usage/by-repository [calls] cc-usage repository 別 breakdown 取得
 */
export function useCcUsageByRepository(params: CcUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccUsage.byRepository(params),
    queryFn: () => ccUsageApi.getByRepository(params),
    enabled: isCcUsageParamsValid(params),
  });
}

/**
 * Claude Code 利用量の team 別 breakdown。
 *
 * チーム別利用量ランキング用。`useCcUsageTeams` (除外フィルタ非適用の選択肢用) とは異なり、
 * scope / 集計対象除外フィルタを適用した集計結果を返す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage by team fetch hook
 * @graph-connects /cc-usage/by-team [calls] cc-usage team 別 breakdown 取得
 */
export function useCcUsageByTeam(params: CcUsageQueryParams) {
  return useQuery({
    queryKey: queryKeys.ccUsage.byTeam(params),
    queryFn: () => ccUsageApi.getByTeam(params),
    enabled: isCcUsageParamsValid(params),
  });
}

/**
 * Claude Code 利用量集計対象チーム一覧。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage teams fetch hook
 * @graph-connects /cc-usage/teams [calls] cc-usage teams 取得
 */
export function useCcUsageTeams(params: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>) {
  return useQuery({
    queryKey: queryKeys.ccUsage.teams(params),
    queryFn: () => ccUsageApi.getTeams(params),
  });
}

/**
 * 集計対象フィルタの選択肢に使う nickname 一覧。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business cc-usage members fetch hook
 * @graph-connects /cc-usage/members [calls] cc-usage members 取得
 */
export function useCcUsageMembers(params: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>) {
  return useQuery({
    queryKey: queryKeys.ccUsage.members(params),
    queryFn: () => ccUsageApi.getMembers(params),
  });
}
